import {
  filter,
  tap,
  ignoreElements,
  switchMap,
  mergeMap,
  catchError,
  concatMap,
} from 'rxjs/operators'
import {of, concat, from} from 'rxjs'
import {Epic} from 'redux-observable'
import {RootState, AppActions} from 'app/rootReducer'
import {SettingsService} from 'services/api'
import {settingsActions} from '../settingsSlice'
import Toast from 'components/Basic/Toast'
import {translations} from 'definitions/externalization'

const inviteAdminsEpic: Epic<AppActions, AppActions, RootState> = action$ =>
  action$.pipe(
    filter(settingsActions.inviteAdmins.match),
    switchMap(({payload}) =>
      from(
        SettingsService.inviteAdmins({
          emailId: payload.emailId,
          role: payload.role,
        }),
      ).pipe(
        concatMap(() => [
          payload.reset(),
          Toast({
            message: `Invitation successfully sent to ${payload.emailId}`,
            type: 'success',
            style: {width: '520px'},
          }),
        ]),
        mergeMap(() => [settingsActions.inviteAdminsSuccess()]),
        catchError((message: string) =>
          concat(of(settingsActions.inviteAdminsFailure(message))),
        ),
      ),
    ),
  )

const inviteAdminsFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(settingsActions.inviteAdminsFailure.match),
    // not possible as it required some error code
    tap(({payload: message}) => Toast({message, type: 'error'})),
    ignoreElements(),
  )

const updateInvitedAdminsEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(settingsActions.updateInvitedAdmins.match),
    switchMap(({payload}) =>
      from(
        SettingsService.updateInvitedAdmins({
          emailId: payload.emailId,
          role: payload.role,
        }),
      ).pipe(
        mergeMap(() => [settingsActions.updateInvitedAdminsSuccess()]),
        catchError((message: string) =>
          concat(of(settingsActions.updateInvitedAdminsFailure(message))),
        ),
      ),
    ),
  )

const updateInvitedAdminsFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(settingsActions.updateInvitedAdminsFailure.match),
    tap(() =>
      Toast({message: translations.UPDATE_INVITED_ADMIN_ERROR, type: 'error'}),
    ),
    ignoreElements(),
  )

const updateInvitedAdminsSuccessEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(settingsActions.updateInvitedAdminsSuccess.match),
    tap(() => {
      Toast({message: translations.UPDATE_INVITED_ADMIN, type: 'success'})
    }),
    ignoreElements(),
  )

const deleteAdminEpic: Epic<AppActions, AppActions, RootState> = action$ =>
  action$.pipe(
    filter(settingsActions.deleteAdmin.match),
    switchMap(({payload}) =>
      from(SettingsService.deleteAdmin(payload.emailId)).pipe(
        concatMap(() => [
          payload.onSuccess(),
          Toast({
            message: `Removed ${
              payload?.userName !== 'Invited' ? payload?.userName : ''
            }(${payload.emailId})`,
            type: 'success',
            style: {width: '500px'},
          }),
        ]),
        mergeMap(() => [settingsActions.deleteAdminSuccess()]),
        catchError((message: string) =>
          concat(of(settingsActions.deleteAdminFailure(message))),
        ),
      ),
    ),
  )

const deleteAdminFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(settingsActions.deleteAdminFailure.match),
    tap(() =>
      Toast({message: translations.DELETE_INVITED_ADMIN_ERROR, type: 'error'}),
    ),
    ignoreElements(),
  )

export const generalEpics = [
  inviteAdminsEpic,
  inviteAdminsFailureEpic,
  deleteAdminEpic,
  deleteAdminFailureEpic,
  updateInvitedAdminsEpic,
  updateInvitedAdminsFailureEpic,
  updateInvitedAdminsSuccessEpic,
]
